/**
 * Branches — system-wide branch ranking (PHASE2_SPEC §4.8).
 *
 * Layout:
 *   KPI tile row · Filter bar · Sortable paginated table.
 * Click a row → /banks/:orgId/branches/:branchIdLocal
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BankBadge from '../../components/BankBadge';
import Card from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import StatusPill from '../../components/StatusPill';
import { formatDate } from '../../helpers/dateFormat';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchBranches, selectBranches } from '../../redux/slices/dashboard';
import type { BranchRow, Flag } from '../../type/api';

const ALL_FLAGS: Flag[] = ['GREEN', 'AMBER', 'RED', 'GREY'];
const PAGE_SIZE = 50;

export default function BranchesPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectBranches);

	const [search, setSearch] = useState('');
	const [enabledFlags, setEnabledFlags] = useState<Set<Flag>>(new Set(ALL_FLAGS));
	const [page, setPage] = useState(0);

	useEffect(() => {
		if (cell.status === 'idle') {
			dispatch(fetchBranches({ limit: 500 }));
		}
	}, [cell.status, dispatch]);

	const data = cell.data;
	const branches: BranchRow[] = useMemo(
		() => (Array.isArray(data?.branches) ? data!.branches : []),
		[data],
	);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return branches.filter((b) => {
			if (!enabledFlags.has(b.npaFlag)) return false;
			if (!q) return true;
			return (
				b.branchName.toLowerCase().includes(q) ||
				b.orgName.toLowerCase().includes(q) ||
				b.branchIdLocal.toLowerCase().includes(q)
			);
		});
	}, [branches, search, enabledFlags]);

	const sort = useSortable<BranchRow>(filtered, { field: 'npaPct', dir: 'desc' });
	const pageCount = Math.max(1, Math.ceil(sort.rows.length / PAGE_SIZE));
	const pagedRows = sort.rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

	useEffect(() => setPage(0), [search, enabledFlags]);

	const toggleFlag = (f: Flag) => {
		setEnabledFlags((prev) => {
			const next = new Set(prev);
			if (next.has(f)) {
				if (next.size > 1) next.delete(f);
			} else {
				next.add(f);
			}
			return next;
		});
	};

	const handleRetry = () => dispatch(fetchBranches({ limit: 500 }));

	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Branches</h1></div></div>
				<Card><LoadingSkeleton rows={6} columns={6} /></Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Branches</h1></div></div>
				<ErrorBanner message={cell.error || 'Failed to load branches'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Branches</h1></div></div>
				<Card><EmptyState title="No branch data" /></Card>
			</div>
		);
	}

	const summary = data.summary;

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">Branches</h1>
					<div className="page-sub">
						{formatCount(summary.branchCount)} branches across {formatCount(summary.banksRepresented)} banks.
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
				<button type="button" className="btn ghost" onClick={handleRetry}>Refresh</button>
			</div>

			<div className="grid cols-4">
				<KpiTile label="Total Branches" value={formatCount(summary.branchCount)} />
				<KpiTile label="Banks Represented" value={formatCount(summary.banksRepresented)} />
				<KpiTile
					label="System Loan ₹Cr"
					value={formatCrore(summary.totalLoanCr, 2)}
					sub={`${formatCrore(summary.totalDepositCr, 2)} deposits`}
				/>
				<KpiTile
					label="Branches > RBI norm"
					value={formatCount(summary.branchesAboveRBI)}
					sub={`${formatCount(summary.branchesInRedZone)} red-zone`}
					flag="AMBER"
					flagVariant="npa"
				/>
			</div>

			<Card title="Filters">
				<div className="filterbar">
					<div className="search">
						<input
							type="search"
							value={search}
							placeholder="Search by branch / bank…"
							onChange={(e) => setSearch(e.target.value)}
							style={{ width: 320 }}
						/>
					</div>
					<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
						{ALL_FLAGS.map((f) => (
							<button
								key={f}
								type="button"
								className={`flag-toggle${enabledFlags.has(f) ? ` active ${f.toLowerCase()}` : ''}`}
								onClick={() => toggleFlag(f)}
							>
								{f}
							</button>
						))}
					</div>
					<div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-faint)' }}>
						{formatCount(filtered.length)} branches
					</div>
				</div>
			</Card>

			<Card title="Branch ranking">
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="orgName" sort={sort}>Bank</SortableTh>
								<SortableTh field="branchName" sort={sort}>Branch</SortableTh>
								<SortableTh field="loanCr" sort={sort} numeric>Loan ₹Cr</SortableTh>
								<SortableTh field="depositCr" sort={sort} numeric>Deposit ₹Cr</SortableTh>
								<SortableTh field="npaCr" sort={sort} numeric>NPA ₹Cr</SortableTh>
								<SortableTh field="npaPct" sort={sort} numeric>NPA %</SortableTh>
								<SortableTh field="kycAccounts" sort={sort} numeric>KYC accts</SortableTh>
								<SortableTh field="uniqueCustomers" sort={sort} numeric>Customers</SortableTh>
								<th>Action</th>
							</tr>
						</thead>
						<tbody>
							{pagedRows.map((b) => (
								<tr
									key={`${b.orgId}-${b.branchIdLocal}`}
									className="clickable"
									onClick={() => navigate(`/banks/${b.orgId}/branches/${encodeURIComponent(b.branchIdLocal)}`)}
									tabIndex={0}
								>
									<td><BankBadge orgId={b.orgId} orgName={b.orgName} /></td>
									<td>
										<div style={{ fontWeight: 600 }}>{b.branchName}</div>
										<div style={{ fontSize: 11, color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>
											ID {b.branchIdLocal}
										</div>
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.loanCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.depositCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.npaCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={b.npaFlag} variant="npa" compact label={formatPct(b.npaPct)} />
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(b.kycAccounts)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(b.uniqueCustomers)}</td>
									<td>
										<button
											type="button"
											className="btn ghost"
											onClick={(e) => {
												e.stopPropagation();
												navigate(`/banks/${b.orgId}/branches/${encodeURIComponent(b.branchIdLocal)}`);
											}}
										>
											View
										</button>
									</td>
								</tr>
							))}
							{!pagedRows.length && (
								<tr><td colSpan={9}><EmptyState title="No branches match" hint="Try clearing the search or flag filters." /></td></tr>
							)}
						</tbody>
					</table>
				</div>
				{pageCount > 1 && (
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
						<span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
							Page {page + 1} of {pageCount}
						</span>
						<div style={{ display: 'flex', gap: 6 }}>
							<button type="button" className="btn ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</button>
							<button type="button" className="btn ghost" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>Next</button>
						</div>
					</div>
				)}
			</Card>
		</div>
	);
}
